import { useState, useCallback, type FormEvent } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { AppDialog } from '../../Common/AppDialog'
import {
  beginContentBreakGlass,
  type BreakGlassReason,
  type BreakGlassGrantResult,
} from '../../../services/ownerConsoleService'
import { navigateOwner, parseOwnerRoute } from '../ownerRouter'

interface BreakGlassModalProps {
  open: boolean
  subject: string
  targetWorkspaceId?: string
  defaultReason?: BreakGlassReason
  defaultLinkedId?: string
  onClose: () => void
  onSuccess: (grant: BreakGlassGrantResult) => void
}

export const BreakGlassModal = ({
  open,
  subject,
  targetWorkspaceId,
  defaultReason = 'SUPPORT_TICKET',
  defaultLinkedId = '',
  onClose,
  onSuccess,
}: BreakGlassModalProps) => {
  const { t } = useNls()
  const [reason, setReason] = useState<BreakGlassReason>(defaultReason)
  const [linkedId, setLinkedId] = useState(defaultLinkedId)
  const [elaboration, setElaboration] = useState('')
  const [durationSec, setDurationSec] = useState<number>(300)
  const [notifyOwner, setNotifyOwner] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = useCallback(async (e?: FormEvent) => {
    if (e) e.preventDefault()
    if (!linkedId.trim()) {
      setError('Linked ID is required.')
      return
    }
    setError(null)
    setSubmitting(true)
    try {
      const res = await beginContentBreakGlass({
        reason,
        linkedId: linkedId.trim(),
        elaboration: elaboration.trim() || undefined,
        subject,
        targetWorkspaceId,
        revealDurationSec: durationSec,
        notifyOwner,
      })
      if (res.ok && res.grantId) {
        try {
          sessionStorage.setItem('owner_breakglass_grant_id', res.grantId)
          sessionStorage.setItem('owner_breakglass_token', res.revealToken)
        } catch {
          // ignore
        }
        // Update URL query with ?bg=1&for=<subject>
        const current = parseOwnerRoute(window.location.pathname, window.location.search)
        const nextQuery = {
          ...current.query,
          bg: '1',
          for: subject,
        }
        navigateOwner({ ...current, query: nextQuery })
        onSuccess(res)
        onClose()
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to begin break-glass reveal.')
    } finally {
      setSubmitting(false)
    }
  }, [reason, linkedId, elaboration, subject, targetWorkspaceId, durationSec, notifyOwner, onSuccess, onClose])

  return (
    <AppDialog
      open={open}
      title={t('NLSOwnerConsoleBreakGlassModalTitle')}
      message={
        <form className="ownerBreakGlassForm" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="bg-target">{t('NLSOwnerConsoleBreakGlassTargetLabel')}</label>
            <div id="bg-target" className="ownerBreakGlassForm__target">{subject}</div>
          </div>

          <div>
            <label htmlFor="bg-reason">{t('NLSOwnerConsoleBreakGlassReasonLabel')}</label>
            <select
              id="bg-reason"
              value={reason}
              onChange={(e) => {
                const next = e.target.value as BreakGlassReason
                setReason(next)
                if (next === 'INCIDENT' || next === 'ABUSE_REVIEW') {
                  setNotifyOwner(true)
                }
              }}
            >
              <option value="SUPPORT_TICKET">SUPPORT_TICKET</option>
              <option value="INCIDENT">INCIDENT</option>
              <option value="ABUSE_REVIEW">ABUSE_REVIEW</option>
              <option value="USER_REQUEST">USER_REQUEST</option>
              <option value="LEGAL_REQUEST">LEGAL_REQUEST</option>
              <option value="DATA_MIGRATION">DATA_MIGRATION</option>
            </select>
          </div>

          <div>
            <label htmlFor="bg-linked-id">{t('NLSOwnerConsoleBreakGlassLinkedIdLabel')}</label>
            <input
              id="bg-linked-id"
              value={linkedId}
              onChange={(e) => setLinkedId(e.target.value)}
              placeholder="e.g. ticket ID, incident key, user ID..."
              required
            />
          </div>

          <div>
            <label htmlFor="bg-elaboration">{t('NLSOwnerConsoleBreakGlassElaborationLabel')}</label>
            <textarea
              id="bg-elaboration"
              value={elaboration}
              onChange={(e) => setElaboration(e.target.value)}
              placeholder="Optional justification note (max 200 chars)"
              maxLength={200}
            />
          </div>

          <div>
            <label htmlFor="bg-duration">{t('NLSOwnerConsoleBreakGlassDurationLabel')}</label>
            <select
              id="bg-duration"
              value={durationSec}
              onChange={(e) => setDurationSec(Number(e.target.value))}
            >
              <option value={300}>5 minutes (default)</option>
              <option value={900}>15 minutes</option>
              <option value={3600}>60 minutes</option>
            </select>
          </div>

          <label style={{ flexDirection: 'row', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={notifyOwner}
              onChange={(e) => setNotifyOwner(e.target.checked)}
            />
            <span>{t('NLSOwnerConsoleBreakGlassNotifyOwner')}</span>
          </label>

          {error && (
            <p style={{ color: 'var(--danger-text, #ef4444)', fontSize: 13, margin: '4px 0' }}>
              {error}
            </p>
          )}
        </form>
      }
      actions={[
        {
          label: t('NLSOwnerConsoleCancel'),
          variant: 'secondary',
          onClick: onClose,
          disabled: submitting,
        },
        {
          label: submitting ? t('NLSOwnerConsoleLoadingData') : t('NLSOwnerConsoleBreakGlassRevealAction'),
          variant: 'primary',
          onClick: () => void handleSubmit(),
          disabled: submitting,
          loading: submitting,
        },
      ]}
      onDismiss={onClose}
    />
  )
}
